import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'

import {Box, Button, Loading, Typography} from '@viptech/react-components'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import {useAuth} from 'src/contexts/AuthContext'
import useSingleCamera from 'src/hooks/api/camera/useSingleCamera'
import NotFoundImage from 'src/images/NotFoundImage'
import {Barriers} from 'src/pages/camera-config/components/camera-barriers/Barriers'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import TabPanel from '../../components/TabPanel'
import {useApp} from '../../layouts/main-layout/contexts/AppContext'
import {cameraConfigStyles} from './CameraConfigBase'
import {CAMERA_TAB_LABELS, CameraOptionsEnum} from './CameraConfigFunctions'
import CameraAdvancedConfig from './components/camera-advanced-config/CameraAdvancedConfig'
import {CameraConfigConnection} from './components/camera-config-connection/CameraConfigConnection'
import CameraConfigHistory from './components/camera-config-historic/CameraConfigHistoric'
import CameraConfigReceiveEvent from './components/camera-config-receive-event/CameraConfigReceiveEvent'
import {CameraGeneralData} from './components/camera-general-data/CameraGeneralData'
import {CameraTabsHeader} from './components/camera-tabs-header/CameraTabsHeader'
import {ObjectDetection} from './components/object-detection/ObjectDetection'
import {CameraConfigProvider} from './context/CameraConfigContext'

function CameraConfigInner() {
  const [tab, setTab] = useState(0)
  const {enqueueSnackbar} = useSnackbar()
  const navigate = useNavigate()

  const hasReadLiveStreamPermission = ValidateUserPermissions('livestream:read')

  useEffect(() => {
    if (!hasReadLiveStreamPermission && (camera?.origin === 'RTSP' || camera?.origin === 'RTMP')) {
      enqueueSnackbar('Você não tem permissão para acessar esse recurso!', {variant: 'warning'})
      navigate('/registers/devices')
    }
  }, [hasReadLiveStreamPermission])

  const {setSubMenuTitleName} = useApp()
  const {id} = useParams<{id?: string}>()

  const {data: camera, isError} = useSingleCamera({
    onError: (error) => handleErrorWithSnackbar(enqueueSnackbar, error, 'Falha ao tentar obter câmera'),
    cameraId: Number(id),
    params: {includes: ['configuration', 'status', 'server', 'client', 'addressInfo', 'company']},
    enabled: !!id,
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const {user, userIsB2c} = useAuth()

  const setDefaultTabParam = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('tab', CAMERA_TAB_LABELS[0])
    setSearchParams(newParams)
  }

  const onChangeTab = (tab: number) => {
    setTab(tab)
    const newParams = new URLSearchParams(searchParams)
    newParams.set('tab', CAMERA_TAB_LABELS[tab])
    newParams.delete('mode')
    setSearchParams(newParams)
  }

  useEffect(() => {
    const tabParam = searchParams.get('tab')

    if (tabParam) {
      const index = CAMERA_TAB_LABELS.indexOf(tabParam)
      if (index !== -1) {
        setTab(index)
      } else {
        setTab(0)
        setDefaultTabParam()
      }
    }
  }, [searchParams])

  useEffect(() => {
    if (!id) return navigate('registers/devices')
    if (userIsB2c && user.role !== 'admin') return navigate(`/camera-config?id=${id}`, {replace: true})
    if (camera) setSubMenuTitleName(camera.name)
  }, [camera])

  return camera === null ? (
    <>
      {!isError && (
        <Box
          height='82vh'
          borderRadius='8px'
          backgroundColor='white'
          padding='20px'
          display='flex'
          justifyContent='center'
          align='center'
          width='100%'
          columnGap='80px'>
          <Loading size='50px' />
        </Box>
      )}
      {isError && (
        <Box
          height='82vh'
          borderRadius='8px'
          backgroundColor='white'
          padding='20px'
          display='flex'
          justifyContent='center'
          align='center'
          width='100%'
          columnGap='80px'>
          <NotFoundImage />

          <Box display='flex' direction='column'>
            <Typography variant='span' size='30px' color='#344054' weight={700} children='Não encontramos a câmera solicitada' />
            <Typography variant='span' children={`Não conseguimos carregar as informações da câmera com id: ${id}.`} />

            <Button marginTop='50px' onClick={() => navigate('/registers/devices')}>
              Voltar para Dispositivos
            </Button>
          </Box>
        </Box>
      )}
    </>
  ) : (
    <Box style={cameraConfigStyles.paper}>
      <CameraTabsHeader tab={tab} setTab={onChangeTab} />

      <Box style={cameraConfigStyles.content}>
        <TabPanel activeTab={tab} tab={CameraOptionsEnum.CAMERA_GENERAL_DATA}>
          <CameraGeneralData />
        </TabPanel>
        <TabPanel activeTab={tab} tab={CameraOptionsEnum.CAMERA_ADVANCED_CONFIG}>
          <CameraAdvancedConfig />
        </TabPanel>
        <TabPanel activeTab={tab} tab={CameraOptionsEnum.CAMERA_CONFIG_LINK_RTSP}>
          <CameraConfigConnection />
        </TabPanel>
        <TabPanel activeTab={tab} tab={CameraOptionsEnum.CAMERA_DETECTION_CONFIG}>
          <Barriers />
        </TabPanel>
        <TabPanel activeTab={tab} tab={CameraOptionsEnum.CAMERA_AREA_DETECTION_CONFIG}>
          <ObjectDetection />
        </TabPanel>
        <TabPanel activeTab={tab} tab={CameraOptionsEnum.CAMERA_CONFIG_RECEIVE_EVENT}>
          <CameraConfigReceiveEvent />
        </TabPanel>
        <TabPanel activeTab={tab} tab={CameraOptionsEnum.CAMERA_CONFIG_HISTORIC}>
          <CameraConfigHistory />
        </TabPanel>
      </Box>
    </Box>
  )
}

export function CameraConfig() {
  return (
    <CameraConfigProvider>
      <CameraConfigInner />
    </CameraConfigProvider>
  )
}
